import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import {
	collection,
	doc,
	getDoc,
	getDocs,
	setDoc,
	Timestamp,
} from 'firebase/firestore';
import { BROKER_GROUP_COLLECTION, firestore } from '@/utils/firebase';
import { getActingUser } from '../user/userApi';
import {
	AUTOMATION_BROKER_STATES_SUBCOLLECTION,
	AUTOMATIONS_COLLECTION,
	mergeBrokerGroupStates,
	normalizeAutomationBrokerState,
	normalizeAutomationCatalogDoc,
} from '@/utils/automationCatalog';

const listTag = (brokerGroupIds = []) => ({
	type: 'AUTOMATION',
	id: `list:${[...brokerGroupIds].sort().join(',') || 'none'}`,
});

const brokerStateRef = (automationId, brokerGroupId) =>
	doc(
		firestore,
		AUTOMATIONS_COLLECTION,
		automationId,
		AUTOMATION_BROKER_STATES_SUBCOLLECTION,
		brokerGroupId,
	);

export const automationsApi = createApi({
	reducerPath: 'automationsApi',
	tagTypes: ['AUTOMATION'],
	baseQuery: fakeBaseQuery(),
	endpoints: (builder) => ({
		listAutomations: builder.query({
			providesTags: (result, error, arg) => [listTag(arg?.brokerGroupIds)],
			queryFn: async ({ brokerGroupIds = [] } = {}) => {
				try {
					const snapshot = await getDocs(collection(firestore, AUTOMATIONS_COLLECTION));
					const catalog = snapshot.docs
						.map((d) =>
							normalizeAutomationCatalogDoc(
								d.id,
								JSON.parse(JSON.stringify(d.data() ?? {})),
							),
						)
						.filter((item) => item.active);

					if (brokerGroupIds.length === 0) {
						return {
							data: catalog.map((item) => ({
								...item,
								...normalizeAutomationBrokerState({ enabled: false }),
							})),
						};
					}

					const withState = await Promise.all(
						catalog.map(async (item) => {
							const states = await Promise.all(
								brokerGroupIds.map(async (brokerGroupId) => {
									const stateSnap = await getDoc(
										brokerStateRef(item.id, brokerGroupId),
									);
									if (!stateSnap.exists()) {
										//Check if item is automation is set to globally enabled
										return normalizeAutomationBrokerState({
											enabled: item.globallyEnabled,
										});
									}
									return normalizeAutomationBrokerState(
										JSON.parse(JSON.stringify(stateSnap.data() ?? {})),
									);
								}),
							);
							return { ...item, ...mergeBrokerGroupStates(states) };
						}),
					);

					return { data: withState };
				} catch (error) {
					console.error('Failed to list automations', error);
					return {
						error: {
							status: 'CUSTOM_ERROR',
							error: error?.message ?? 'Failed to list automations',
						},
					};
				}
			},
		}),
		setAutomationEnabled: builder.mutation({
			invalidatesTags: [{ type: 'AUTOMATION' }],
			queryFn: async ({ automationId, brokerGroupIds = [], enabled }, { dispatch, getState }) => {
				try {
					if (!automationId || brokerGroupIds.length === 0) {
						return {
							error: {
								status: 'CUSTOM_ERROR',
								error: 'Automation and broker groups are required',
							},
						};
					}

					const performer = await getActingUser(dispatch, getState);
					const userId = performer.userId;

					const now = Timestamp.now();

					await Promise.all(
						brokerGroupIds.map(async (brokerGroupId) => {
							const ref = brokerStateRef(automationId, brokerGroupId);
							const existing = await getDoc(ref);
							const existingData = existing.exists() ? existing.data() ?? {} : {};

							await setDoc(
								ref,
								{
									brokerGroupId,
									brokerGroup: doc(
										firestore,
										BROKER_GROUP_COLLECTION,
										brokerGroupId,
									),
									enabled: Boolean(enabled),
									lastModified: now,
									lastModifiedBy: userId,
									lastTriggered: existingData.lastTriggered ?? null,
									triggerCount: existingData.triggerCount ?? 0,
								},
								{ merge: true },
							);
						}),
					);

					return {
						data: {
							automationId,
							brokerGroupIds,
							enabled: Boolean(enabled),
						},
					};
				} catch (error) {
					console.error('Failed to update automation enabled state', error);
					return {
						error: {
							status: 'CUSTOM_ERROR',
							error: error?.message ?? 'Failed to update automation',
						},
					};
				}
			},
		}),
	}),
});

export const { useListAutomationsQuery, useSetAutomationEnabledMutation } = automationsApi;
